/**
 * Client-side validation shaped like the API's.
 *
 * The backend returns failures as `backend.ResponseErrors`, a Go
 * `map[string]string` of field name -> sentence. `validate()` returns that same
 * shape, so client and server errors feed one `Errors` object and render
 * through the same field components with nothing extra wired up per field.
 *
 * A rule is `(value, allData) => string | null`; a string means invalid.
 *
 *     validate(form.data(), { provider_id: required('Please select a valid Provider.') })
 */

// Arrays matter here: Buefy's checkbox group leaves holes, so an untouched
// serviceType arrives as [null].
export function isBlank(value) {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  if (Array.isArray(value)) {
    return value.filter((v) => v !== null && v !== undefined && v !== '').length === 0;
  }
  return false;
}

export const required =
  (message = 'This field is required.') =>
  (value) =>
    isBlank(value) ? message : null;

// Same check as required(), separate only so the default message reads
// correctly for a group of options.
export const requiredAny =
  (message = 'Please select at least one option.') =>
  (value) =>
    isBlank(value) ? message : null;

// Returns {field: message}, empty when valid. First failing rule per field
// wins, matching the server's one-message-per-field shape.
export function validate(data, rules) {
  const errors = {};
  for (const field of Object.keys(rules)) {
    const fieldRules = [].concat(rules[field]);
    for (const rule of fieldRules) {
      const message = rule(data[field], data);
      if (message) {
        errors[field] = message;
        break;
      }
    }
  }
  return errors;
}
